import RouteAttributes from '@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity';
import AddressAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity';

/**
 * Data passed to the AssignTerritoryModal when opened
 */
export interface AssignTerritoryModalData {
  clientId: string; // MongoDB _id del cliente
}

/**
 * Result returned when the modal closes
 */
export interface AssignTerritoryModalResult {
  success: boolean;
  addressId?: string;
  addressIds?: string[];
  territoriesAssigned?: number;
  addressesUpdated?: number;
}

/**
 * Territory row for the paginated table selection
 */
export interface TerritoryRow {
  _id: string;
  id: number;
  name: string;
  code: string;
  companyId: number;
  companyName: string;
  isAlreadyAssigned?: boolean;
}

/**
 * Territory row view with selection state
 * (isSelected se actualiza cuando se regenera la lista)
 */
export interface TerritoryRowView extends TerritoryRow {
  isSelected: boolean;
}

/**
 * Simplified address for display in the modal
 */
export interface AddressOption {
  _id: string;
  name: string;
  address: string;
  routesCount: number;
  isPrincipal: boolean;
}

/**
 * Simplified route info for display in address row
 */
export interface AddressRouteInfo {
  _id: string;
  name: string;
  code?: string;
}

/**
 * Address row for the addresses table selection
 */
export interface AddressRow {
  _id: string;
  name: string;
  address: string;
  routesCount: number;
  isPrincipal: boolean;
  routes: AddressRouteInfo[];
}

/**
 * Address row view with computed display properties
 */
export interface AddressRowView extends AddressRow {
  isSelected: boolean;
  visibleRoutes: AddressRouteInfo[];
  hasHiddenRoutes: boolean;
  hiddenRoutesCount: number;
}

/**
 * Factory function to create TerritoryRowView
 */
export function createTerritoryRowView(
  route: RouteAttributes,
  assignedIds: Set<string>,
  isSelected: boolean
): TerritoryRowView {
  return {
    _id: route._id.toString(),
    id: route.id,
    name: route.name,
    code: route.externalKeys?.code || '',
    companyId: route.company?.id || 0,
    companyName: route.company?.name || '',
    isAlreadyAssigned: assignedIds.has(route._id.toString()),
    isSelected,
  };
}

/**
 * Factory function to create AddressRowView
 */
export function createAddressRowView(
  addr: AddressAttributes,
  isSelected: boolean,
  maxVisible: number = 3
): AddressRowView {
  const routes: AddressRouteInfo[] = (addr.routes || []).map(r => ({
    _id: r._id?.toString() || '',
    name: r.name || '',
    code: r.externalKeys?.code || '',
  }));

  return {
    _id: addr._id?.toString() || '',
    name: addr.name || '',
    address: addr.address || '',
    routesCount: routes.length,
    isPrincipal: addr.isPrincipal || false,
    routes,
    isSelected,
    visibleRoutes: routes.slice(0, maxVisible),
    hasHiddenRoutes: routes.length > maxVisible,
    hiddenRoutesCount: Math.max(0, routes.length - maxVisible),
  };
}
